import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { onlineManager, useIsMutating, useQueryClient } from '@tanstack/react-query';
import type { CatalogRecord } from '@svetoch/contracts';
import { api, errorMessage } from './api';
import { DATA_FRESHNESS_MS } from './query-config';

export type DataFreshnessTarget = { kind: 'record'; id: string } | { kind: 'catalog' };
export type DataFreshnessState = 'fetching' | 'stale' | 'fresh';
export type DataFreshnessReason = 'fetching' | 'pending' | 'offline' | 'error' | 'not-loaded' | 'invalidated' | 'expired' | 'fresh';

const subscribeOnline = (listener: () => void) => onlineManager.subscribe(listener);
const getOnline = () => onlineManager.isOnline();
const getServerOnline = () => true;

/**
 * Observes existing query state without mounting a QueryObserver or loading data.
 * Opening a saved sidebar entry therefore does not fetch every other entry.
 */
export function useDataFreshness(target: DataFreshnessTarget) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const recordId = target.kind === 'record' ? target.id : undefined;
  const kind = target.kind;
  const pendingWrites = useIsMutating({ predicate: (mutation) => {
    const key = mutation.options.mutationKey;
    if (key?.[0] === 'record-write') return kind === 'catalog' || key[1] === recordId;
    if (key?.[0] !== 'record-favorite') return false;
    if (kind === 'catalog') return true;
    const variables = mutation.state.variables;
    return !!variables && typeof variables === 'object' && 'id' in variables && variables.id === recordId;
  } });
  const selectQuery = useCallback(() => {
    if (kind === 'record') return cache.find({ queryKey: ['record', recordId], exact: true });
    return cache.findAll({ queryKey: ['records'], type: 'active' })[0];
  }, [cache, kind, recordId]);
  const subscribeCache = useCallback((listener: () => void) => cache.subscribe(listener), [cache]);
  const getSnapshot = useCallback(() => selectQuery()?.state ?? null, [selectQuery]);
  const snapshot = useSyncExternalStore(subscribeCache, getSnapshot, () => null);
  const online = useSyncExternalStore(subscribeOnline, getOnline, getServerOnline);
  const [, tick] = useState(0);
  const updatedAt = snapshot?.dataUpdatedAt || null;

  // TanStack does not emit a cache event merely because wall-clock time passes.
  // Schedule one render at the expiry boundary; no polling or network request.
  useEffect(() => {
    if (!updatedAt) return;
    const remaining = updatedAt + DATA_FRESHNESS_MS - Date.now();
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => tick((value) => value + 1), remaining + 1);
    return () => window.clearTimeout(timer);
  }, [updatedAt]);

  let state: DataFreshnessState;
  let reason: DataFreshnessReason;
  let tooltipTitle: string;
  let detail: string;
  if (!online) {
    state = 'stale'; reason = 'offline';
    tooltipTitle = 'Нет соединения';
    detail = 'Подключитесь к сети, затем обновите данные.';
  } else if (pendingWrites > 0) {
    state = 'fetching'; reason = 'pending';
    tooltipTitle = 'Сохранение изменений';
    detail = 'Изменения отправляются в API. Подождите завершения.';
  } else if (snapshot?.fetchStatus === 'fetching') {
    state = 'fetching'; reason = 'fetching';
    tooltipTitle = 'Обновление данных';
    detail = 'Выполняется запрос к API. Подождите завершения.';
  } else if (snapshot?.status === 'pending' && snapshot.fetchStatus !== 'idle') {
    state = 'fetching'; reason = 'pending';
    tooltipTitle = 'Загрузка данных';
    detail = snapshot.fetchStatus === 'paused' ? 'Запрос ожидает восстановления соединения.' : 'Ожидаем данные от API.';
  } else if (snapshot?.status === 'error') {
    state = 'stale'; reason = 'error';
    tooltipTitle = 'Не удалось обновить данные';
    detail = errorMessage(snapshot.error);
  } else if (!snapshot || !updatedAt || snapshot.data === undefined) {
    state = 'stale'; reason = 'not-loaded';
    tooltipTitle = 'Данные ещё не загружены';
    detail = kind === 'record' ? 'Откройте или обновите запись.' : 'Откройте каталог, чтобы загрузить список.';
  } else if (snapshot.isInvalidated) {
    state = 'stale'; reason = 'invalidated';
    tooltipTitle = 'Данные требуют обновления';
    detail = 'Локальная копия отмечена приложением для повторной загрузки.';
  } else if (Date.now() - updatedAt >= DATA_FRESHNESS_MS) {
    state = 'stale'; reason = 'expired';
    tooltipTitle = 'Срок актуальности истёк';
    detail = 'С последней успешной загрузки прошло 60 секунд или больше.';
  } else {
    state = 'fresh'; reason = 'fresh';
    tooltipTitle = 'Данные актуальны';
    detail = 'Последняя успешная загрузка была менее 60 секунд назад.';
  }

  const query = selectQuery();
  const refreshing = state === 'fetching';
  const canRefresh = online && !refreshing && (kind === 'record' || !!query);
  const loadedTime = updatedAt
    ? `Последняя загрузка: ${new Date(updatedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.`
    : '';
  const tooltipDescription = [
    detail,
    loadedTime,
    'Срок актуальности — 60 секунд. Изменения на сервере автоматически не отслеживаются.',
    canRefresh ? 'Нажмите на индикатор, чтобы обновить.' : '',
  ].filter(Boolean).join(' ');

  const refresh = useCallback(async () => {
    if (!onlineManager.isOnline() || pendingWrites > 0) return;
    const current = selectQuery();
    if (current?.state.fetchStatus === 'fetching') return;
    if (kind === 'record' && recordId && (!current || current.isDisabled() || typeof current.options.queryFn !== 'function')) {
      // Metadata can exist without a queryFn; an aborted first load can leave
      // an inactive, disabled query that refetchQueries would otherwise skip.
      // Mark that copy stale so a manual click really requests the server.
      if (current) await queryClient.invalidateQueries({ queryKey: ['record', recordId], exact: true, refetchType: 'none' });
      await queryClient.fetchQuery({
        queryKey: ['record', recordId],
        queryFn: () => api.getRecord(recordId),
        staleTime: DATA_FRESHNESS_MS,
      });
      return;
    }
    if (current) {
      await queryClient.refetchQueries(
        { queryKey: current.queryKey, exact: true, type: 'all' },
        { throwOnError: true, cancelRefetch: false },
      );
    }
  }, [kind, pendingWrites, queryClient, recordId, selectQuery]);

  return {
    state, reason, updatedAt, refreshing, canRefresh,
    tooltipTitle, tooltipDescription, refresh,
    cachedRecord: kind === 'record' ? snapshot?.data as CatalogRecord | undefined : undefined,
  };
}
